import { Button, Tag } from '@carbon/react';
import { ListTable, StatTile, StatTiles, toDateValue, useFormat } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { hoApi } from '../api';
import { ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, type ReportRange } from './reportRange';

type Day = HoComponents['schemas']['EinvoiceReconDayDto'];

const n = (value: number | string | null | undefined) => Number(value ?? 0);

function thisMonth(): ReportRange {
  const today = new Date();
  return { from: toDateValue(new Date(today.getFullYear(), today.getMonth(), 1)), to: toDateValue(today), storeId: '' };
}

/**
 * E-invoice reconciliation (REPORTS.md D4): per store and payment date, the paid orders against their e-invoices, so the
 * accountant sees what is issued, still waiting, failed, missing or not set up, and what must be adjusted after a refund.
 */
export function EinvoiceReconPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useState<ReportRange>(thisMonth);
  const r = (key: string, options?: Record<string, unknown>) => t(`reports.einvoices.${key}` as I18nKey, options);
  const recon = useQuery({
    queryKey: ['reports', 'einvoices', range],
    queryFn: () => unwrap<Day[]>(hoApi.GET('/api/v1/reporting/einvoices', { params: { query: rangeQuery(range) } })),
  });
  const days = recon.data ?? [];
  const sum = (pick: (d: Day) => number | string) => days.reduce((s, d) => s + n(pick(d)), 0);
  const paid = sum((d) => d.paidTotal);
  const problems = (d: Day) => n(d.failed) + n(d.missing) + n(d.notConfigured) + n(d.needsAdjustment);

  return (
    <ReportFrame
      title={r('title')}
      description={r('description')}
      range={range}
      onRange={setRange}
      error={recon.error}
      csvName="einvoice-reconciliation"
      csv={
        days.length > 0
          ? () => [
              [
                r('date'),
                r('store'),
                r('paidOrders'),
                r('paidTotal'),
                r('issued'),
                r('issuedAmount'),
                r('waiting'),
                r('failed'),
                r('missing'),
                r('notConfigured'),
                r('company'),
                r('toAdjust'),
              ],
              ...days.map((d) => [
                d.date,
                d.storeName || d.storeCode,
                n(d.paidOrders),
                n(d.paidTotal),
                n(d.issued),
                n(d.issuedAmount),
                n(d.waiting),
                n(d.failed),
                n(d.missing),
                n(d.notConfigured),
                n(d.company),
                n(d.needsAdjustment),
              ]),
            ]
          : undefined
      }
    >
      <StatTiles>
        <StatTile
          label={r('issuedTile')}
          value={format.number(sum((d) => d.issuedAmount))}
          caption={r('ofPaid', {
            count: sum((d) => d.issued),
            orders: sum((d) => d.paidOrders),
            paid: format.number(paid),
          })}
          isLoading={recon.isLoading}
        />
        <StatTile
          label={r('waiting')}
          value={format.number(sum((d) => d.waitingAmount))}
          caption={r('count', { count: sum((d) => d.waiting) })}
          isLoading={recon.isLoading}
        />
        <StatTile
          label={r('toCheck')}
          value={format.number(sum((d) => d.failed) + sum((d) => d.missing) + sum((d) => d.notConfigured))}
          caption={r('toCheckCaption', {
            failed: sum((d) => d.failed),
            missing: sum((d) => d.missing),
            notConfigured: sum((d) => d.notConfigured),
          })}
          isLoading={recon.isLoading}
        />
        <StatTile
          label={r('toAdjust')}
          value={format.number(sum((d) => d.needsAdjustment))}
          caption={r('toAdjustCaption')}
          isLoading={recon.isLoading}
        />
      </StatTiles>

      <ListTable<Day & { id: string }>
        title={r('byDay')}
        description={r('note')}
        toolbarActions={
          <Button kind="ghost" as={RouterLink} to="/einvoice/invoices">
            {r('openInvoices')}
          </Button>
        }
        columns={[
          { key: 'date', header: r('date'), render: (d) => format.date(d.date) },
          { key: 'storeName', header: r('store'), render: (d) => d.storeName || d.storeCode },
          {
            key: 'paidOrders',
            header: r('paidOrders'),
            numeric: true,
            render: (d) => `${format.number(n(d.paidOrders))} · ${format.number(n(d.paidTotal))}`,
          },
          {
            key: 'issued',
            header: r('issued'),
            numeric: true,
            render: (d) => `${format.number(n(d.issued))} · ${format.number(n(d.issuedAmount))}`,
          },
          {
            key: 'waiting',
            header: r('waiting'),
            numeric: true,
            render: (d) => (n(d.waiting) ? format.number(n(d.waiting)) : ''),
          },
          {
            key: 'company',
            header: r('company'),
            numeric: true,
            render: (d) => (n(d.company) ? format.number(n(d.company)) : ''),
          },
          {
            key: 'problems',
            header: r('problems'),
            render: (d) =>
              problems(d) === 0 ? (
                <Tag type="green">{r('ok')}</Tag>
              ) : (
                <>
                  {n(d.failed) > 0 && <Tag type="red">{r('failedTag', { count: n(d.failed) })}</Tag>}
                  {n(d.missing) > 0 && <Tag type="red">{r('missingTag', { count: n(d.missing) })}</Tag>}
                  {n(d.notConfigured) > 0 && (
                    <Tag type="magenta">{r('notConfiguredTag', { count: n(d.notConfigured) })}</Tag>
                  )}
                  {n(d.needsAdjustment) > 0 && (
                    <Tag type="purple">{r('adjustTag', { count: n(d.needsAdjustment) })}</Tag>
                  )}
                </>
              ),
          },
        ]}
        rows={days.map((d) => ({ ...d, id: `${d.storeId}-${d.date}` }))}
        ariaLabel={r('title')}
        loading={recon.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
    </ReportFrame>
  );
}
